// Extracted verbatim from pages/coach/Schedule.jsx (2026-06-08 P3 refactor).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    DAY_LABELS, DAY_LABELS_LONG, HALF_HOUR_TIMES, formatTimeDisplay,
    isoDate, startOfWeekMon, addDays, weekRangeLabel, jsDayToMonIdx,
} from "@/lib/schedule";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import { ModalShell, Field, inputClass } from "./ModalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TemplateModal({ draft, setDraft, onSave, onClose }) {
    const days = draft.days_of_week || [];
    const times = draft.start_times || [];

    const toggleDay = (i) => {
        setDraft({
            ...draft,
            days_of_week: days.includes(i) ? days.filter((d) => d !== i) : [...days, i].sort((a, b) => a - b),
        });
    };
    const toggleTime = (t) => {
        setDraft({
            ...draft,
            start_times: times.includes(t) ? times.filter((x) => x !== t) : [...times, t].sort(),
        });
    };
    const selectAllDays = () => setDraft({ ...draft, days_of_week: [0, 1, 2, 3, 4, 5, 6] });
    const clearDays = () => setDraft({ ...draft, days_of_week: [] });
    const selectWeekdays = () => setDraft({ ...draft, days_of_week: [0, 1, 2, 3, 4] });
    const clearTimes = () => setDraft({ ...draft, start_times: [] });

    // Time grid is half-hour increments from 5 AM through 8 PM (last selectable
    // start = 8:00 PM). Matches MaxSC's posted hours window with a small buffer.
    const HOURS = Array.from({ length: 16 }, (_, i) => i + 5); // 5..20
    const previewCount = days.length * times.length;

    return (
        <ModalShell onClose={onClose} testId="schedule-template-modal" title={draft.template_id ? "Edit template" : "New template"}>
            <Field label="Class name">
                <input
                    data-testid="tpl-label"
                    className={inputClass}
                    value={draft.label}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                    placeholder="Small Group"
                />
            </Field>

            <Field label={`Days of week · ${days.length} selected`}>
                <div className="flex items-center gap-2 mb-2">
                    <button type="button" onClick={selectWeekdays} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-200 hover:border-brand-red">M – F</button>
                    <button type="button" onClick={selectAllDays} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-200 hover:border-brand-red">All 7</button>
                    <button type="button" onClick={clearDays} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:text-white">Clear</button>
                </div>
                <div className="grid grid-cols-7 gap-2">
                    {DAY_LABELS_LONG.map((d, i) => {
                        const sel = days.includes(i);
                        return (
                            <button
                                key={d}
                                type="button"
                                data-testid={`tpl-day-${i}`}
                                onClick={() => toggleDay(i)}
                                className={`py-2 text-xs uppercase tracking-wider border transition ${
                                    sel
                                        ? "border-brand-red text-brand-red bg-brand-red/10"
                                        : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                                }`}
                            >
                                {d.slice(0, 3)}
                            </button>
                        );
                    })}
                </div>
            </Field>

            <Field label={`Start times · ${times.length} selected`}>
                <div className="flex items-center gap-2 mb-2">
                    <button type="button" onClick={clearTimes} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:text-white">Clear all</button>
                    <span className="text-[11px] text-zinc-400">5 AM – 8 PM · half-hour increments</span>
                </div>
                <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg p-2 bg-black/40">
                    <div className="grid grid-cols-1 gap-1">
                        {HOURS.map((h) => {
                            const t1 = `${String(h).padStart(2, "0")}:00`;
                            const t2 = `${String(h).padStart(2, "0")}:30`;
                            return (
                                <div key={h} className="grid grid-cols-2 gap-1">
                                    {[t1, t2].map((t) => {
                                        const sel = times.includes(t);
                                        return (
                                            <button
                                                key={t}
                                                type="button"
                                                data-testid={`tpl-time-${t}`}
                                                onClick={() => toggleTime(t)}
                                                className={`py-1.5 text-[12px] tabular-nums border transition rounded ${
                                                    sel
                                                        ? "border-brand-red text-brand-red bg-brand-red/10"
                                                        : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                                }`}
                                            >
                                                {formatTimeDisplay(t)}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Duration (min)">
                    <input
                        data-testid="tpl-duration"
                        type="number"
                        min={15}
                        step={15}
                        className={inputClass}
                        value={draft.duration_minutes}
                        onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Capacity (coach-only)">
                    <input
                        data-testid="tpl-capacity"
                        type="number"
                        min={1}
                        className={inputClass}
                        value={draft.capacity}
                        onChange={(e) => setDraft({ ...draft, capacity: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Cancel window (hours)">
                    <input
                        data-testid="tpl-cancel"
                        type="number"
                        min={0}
                        className={inputClass}
                        value={draft.cancel_window_hours}
                        onChange={(e) => setDraft({ ...draft, cancel_window_hours: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Category (optional)">
                    <select
                        data-testid="tpl-category"
                        className={inputClass}
                        value={draft.category || ""}
                        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    >
                        <option value="">— None —</option>
                        {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                    </select>
                </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input
                    type="checkbox"
                    data-testid="tpl-active"
                    checked={draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                Active (inactive templates can't be published)
            </label>

            <div className="border border-brand-navy-900 bg-brand-navy-950/40 rounded-lg px-3 py-2 text-xs text-zinc-200">
                Each "Publish" run will create up to <strong className="text-white">{previewCount} slot{previewCount === 1 ? "" : "s"} per week</strong> ({days.length} day{days.length === 1 ? "" : "s"} × {times.length} time{times.length === 1 ? "" : "s"}).
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="btn-ghost-tactical">Cancel</button>
                <button
                    type="button"
                    data-testid="tpl-save"
                    onClick={onSave}
                    disabled={days.length === 0 || times.length === 0}
                    className="btn-tactical disabled:opacity-50"
                >
                    Save template
                </button>
            </div>
        </ModalShell>
    );
}

export function CloseDayModal({ draft, setDraft, onSave, onClose }) {
    const niceDate = new Date(`${draft.date}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
    return (
        <ModalShell onClose={onClose} testId="closure-modal" title={niceDate}>
            <label className="flex items-center gap-2 text-sm text-zinc-200">
                <input
                    type="checkbox"
                    data-testid="closure-toggle"
                    checked={draft.isClosed}
                    onChange={(e) => setDraft({ ...draft, isClosed: e.target.checked })}
                />
                Mark this day as <strong className="text-white">closed</strong> (cancels all slots that day)
            </label>
            {draft.isClosed && (
                <Field label="Reason / note">
                    <input
                        data-testid="closure-note"
                        className={inputClass}
                        value={draft.note}
                        onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                        placeholder="e.g. Christmas — gym closed"
                    />
                </Field>
            )}
            <p className="text-[11px] text-zinc-400">
                Closed days are skipped when publishing recurring templates and existing slots on
                that day get cancelled. Re-open by un-checking the box.
            </p>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="btn-ghost-tactical">Cancel</button>
                <button type="button" data-testid="closure-save" onClick={onSave} className="btn-tactical">Save</button>
            </div>
        </ModalShell>
    );
}

export function SlotModal({ draft, setDraft, onSave, onClose }) {
    return (
        <ModalShell onClose={onClose} testId="schedule-slot-modal" title={draft.slot_id ? "Edit slot" : "New one-off slot"}>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Date">
                    <input
                        data-testid="slot-date"
                        type="date"
                        className={inputClass}
                        value={draft.date}
                        onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                    />
                </Field>
                <Field label="Start time">
                    <select
                        data-testid="slot-start"
                        className={inputClass}
                        value={draft.start_time}
                        onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
                    >
                        {HALF_HOUR_TIMES.map((t) => (<option key={t} value={t}>{formatTimeDisplay(t)}</option>))}
                    </select>
                </Field>
                <Field label="Duration (min)">
                    <input
                        type="number"
                        min={15}
                        step={15}
                        className={inputClass}
                        value={draft.duration_minutes}
                        onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Capacity">
                    <input
                        data-testid="slot-capacity"
                        type="number"
                        min={1}
                        className={inputClass}
                        value={draft.capacity}
                        onChange={(e) => setDraft({ ...draft, capacity: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Cancel window (hrs)">
                    <input
                        type="number"
                        min={0}
                        className={inputClass}
                        value={draft.cancel_window_hours}
                        onChange={(e) => setDraft({ ...draft, cancel_window_hours: Number(e.target.value) })}
                    />
                </Field>
                <Field label="Category">
                    <select
                        className={inputClass}
                        value={draft.category || ""}
                        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    >
                        <option value="">— None —</option>
                        {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                    </select>
                </Field>
            </div>
            <Field label="Label (optional)">
                <input
                    className={inputClass}
                    value={draft.label || ""}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
            </Field>
            <Field label="Notes (coach-only)">
                <textarea
                    rows={2}
                    className={inputClass}
                    value={draft.notes || ""}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="btn-ghost-tactical">Cancel</button>
                <button type="button" data-testid="slot-save" onClick={onSave} className="btn-tactical">Save slot</button>
            </div>
        </ModalShell>
    );
}

export function PublishModal({ draft, setDraft, onSave, onClose }) {
    return (
        <ModalShell onClose={onClose} testId="schedule-publish-modal" title="Publish template across dates">
            <p className="text-sm text-zinc-200">
                Generates a slot for every matching weekday in this date range. Existing slots from the same template are skipped.
            </p>
            <div className="grid grid-cols-2 gap-3">
                <Field label="From date">
                    <input
                        data-testid="publish-from"
                        type="date"
                        className={inputClass}
                        value={draft.from_date}
                        onChange={(e) => setDraft({ ...draft, from_date: e.target.value })}
                    />
                </Field>
                <Field label="To date">
                    <input
                        data-testid="publish-to"
                        type="date"
                        className={inputClass}
                        value={draft.to_date}
                        onChange={(e) => setDraft({ ...draft, to_date: e.target.value })}
                    />
                </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="btn-ghost-tactical">Cancel</button>
                <button type="button" data-testid="publish-save" onClick={onSave} className="btn-tactical">Publish</button>
            </div>
        </ModalShell>
    );
}

